import { ScrollingModule } from '@angular/cdk/scrolling';
import { AsyncPipe, NgStyle, formatDate } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    LOCALE_ID,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordionTogglePosition, MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule } from '@angular/material/legacy-card';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, filter, map, share, switchMap, tap } from 'rxjs/operators';
import { EditProfessionDialogComponent } from 'src/app/facility-contracts/facilities/details/components/dialogs/edit-profession-dialog.component';
import { BillingRateType } from 'src/app/facility-contracts/facilities/models/billing-rate-type.enum';
import { FacilityBillingRateModel } from 'src/app/facility-contracts/facilities/models/facility-billing-rate-model';
import { FacilityBillingRateType } from 'src/app/facility-contracts/facilities/models/facility-billing-rate-type.enum';
import { FacilityBillingRate } from 'src/app/facility-contracts/facilities/models/facility-billing-rate.model';
import { FacilityBillingShiftDifferential } from 'src/app/facility-contracts/facilities/models/facility-billing-shift-differential.model';
import { FacilityProfession } from 'src/app/facility-contracts/facilities/models/facility-profession.model';
import { FacilityBillingService } from 'src/app/facility-contracts/facility-billing.service';
import { BannerComponent } from 'src/app/shared/banner.component';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { IsEmptyPipe } from 'src/app/shared/pipes/is-empty.pipe';
import { ConfirmDialogComponent, ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { ToasterService } from 'src/app/shared/services/toaster.service';

@UntilDestroy()
@Component({
    selector: 'nova-billing-rates',
    templateUrl: './billing-rates.component.html',
    styleUrls: ['../../../../styles.scss', './billing-rates.component.scss'],
    standalone: true,
    imports: [
        MatExpansionModule,
        BannerComponent,
        MatLegacyProgressBarModule,
        MatLegacyFormFieldModule,
        ScrollingModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        MatLegacyButtonModule,
        NgStyle,
        MatLegacyCardModule,
        MatLegacySelectModule,
        IsEmptyPipe,
        AsyncPipe,
        MatTooltipModule
     ]
})
export class BillingRatesComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input()
    facilityId: number;

    @Input()
    editable = true;

    @Input()
    reloadBilling$: Observable<number>;

    @Output()
    editMode = new EventEmitter<boolean>();

    billingRatesCount = 0;

    isLoading = false;

    canEditProfession = true;

    deletedShiftDifferential = false;

    dataEdited = false;

    featureFlag = FeatureFlag;

    localBillingRates = [];

    lastModified: Date;

    facilityBillingRateType = FacilityBillingRateType;

    searchText = '';

    billingRates$: Observable<FacilityBillingRateModel[]>;

    reload$ = new BehaviorSubject(true);

    expansionTogglePosition: MatAccordionTogglePosition = 'before';

    private readonly _cleanup$ = new Subject();

    maxRangeValue = 9999.99;
    minRangeValue = 0;

    private _height: string;

    private readonly panelClass = 'nova__dialog';
    private readonly panelClassSm = 'nova__dialog--sm';

    constructor(
        private readonly facilityBillingService: FacilityBillingService,
        private readonly confirmDialogService: ConfirmDialogService,
        private readonly dialog: MatDialog,
        private readonly changeDetectRef: ChangeDetectorRef,
        private readonly toasterService: ToasterService,
        private readonly featureManager: LDFeatureManager,
        @Inject(LOCALE_ID) private readonly locale: string
    ) {}

    ngOnInit(): void {
        this.loadRates();
        // Reload
        if (this.reloadBilling$) {
            this.reloadBilling$.pipe(untilDestroyed(this)).subscribe(() => {
                this.reload$.next(true);
            });
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(): void {
        // set the viewport to 70% of window's height
        this._height = `${Math.ceil(window.innerHeight * 0.7)}px`;
    }

    get height() {
        return this._height;
    }

    ngAfterViewInit(): void {
        this.isLoading = true;
        this.onResize();

        this.changeDetectRef.detectChanges();
    }

    loadRates() {
        const rates$ = this.facilityBillingService.getBillingRatesByFacilityId(this.facilityId).pipe(
            map((rates) => {
                this.billingRatesCount = rates ? rates.length : 0;
                this.localBillingRates = [];
                return rates.map((rate) => {
                    if (
                        this.lastModified === undefined ||
                        this.lastModified === null ||
                        (rate.modifiedOn !== undefined && this.lastModified < new Date(rate.modifiedOn))
                    ) {
                        this.lastModified = new Date(rate.modifiedOn);
                    }
                    this.localBillingRates = this.localBillingRates.concat(rate);
                    return new FacilityBillingRateModel(rate);
                });
            }),
            share()
        );

        this.billingRates$ = this.reload$.pipe(
            tap(() => (this.isLoading = true)),
            switchMap(() => rates$.pipe()),
            tap(() => {
                // changes are made to the billing rates, see if Default Shift data needs to be setup
                this.isLoading = false;
                this.setupDefaultShiftData();
            }),
            catchError(() => {
                this.isLoading = false;
                this.toasterService.toast('An error occurred while getting billing rates.');
                return of([] as FacilityBillingRateModel[]);
            })
        );
    }

    isTypeChecked(
        shifts: FacilityBillingShiftDifferential[],
        shift: FacilityBillingShiftDifferential,
        type: FacilityBillingRateType
    ): boolean {
        let result = true;

        if (type === this.facilityBillingRateType.day) {
            result = shifts.some((x) => x.day && x !== shift);
        }

        if (type === this.facilityBillingRateType.mid) {
            result = shifts.some((x) => x.mid && x !== shift);
        }

        if (type === this.facilityBillingRateType.night) {
            result = shifts.some((x) => x.night && x !== shift);
        }

        if (type === this.facilityBillingRateType.weekendDay) {
            result = shifts.some((x) => x.weekendDay && x !== shift);
        }

        if (type === this.facilityBillingRateType.weekendMid) {
            result = shifts.some((x) => x.weekendMid && x !== shift);
        }

        if (type === this.facilityBillingRateType.weekendNight) {
            result = shifts.some((x) => x.weekendNight && x !== shift);
        }

        return result;
    }

    formatDateString(dateParam: string | Date): string {
        const localDate = new Date(dateParam);
        return formatDate(localDate, 'MM/dd/yyyy h:mm a', this.locale);
    }

    validateNumeric(event = null, max: number, isDecimal: boolean): boolean {
        const lowLimit = 31;
        const lowDigitLimit = 48;
        const highDigitLimit = 57;

        let result = true;

        if (!isDecimal) {
            const charCode = event.which ? event.which : event.keyCode;
            if (charCode > lowLimit && (charCode < lowDigitLimit || charCode > highDigitLimit)) {
                result = false;
            }
        }

        if (isNaN(event.target.value)) {
            event.target.value = event.target.value.replace(/[^\d.-]/g, '');
            result = false;
        }

        if (parseInt(event.target.value) > max) {
            event.target.value = max;
        }

        return result;
    }

    hasRangeBillingError(isRangeBilling: boolean, min: number, max: number): boolean {
        let result = false;

        if (isRangeBilling) {
            result = min > max;
        }

        return result;
    }

    /**
     * Used to determine if SAVE button is enabled or disabled.  It checks the following values:
     * 1) For all Billing Range, min value <= max value
     * 2) All values must be within a certain range.
     */
    hasRangeBillingErrors(rates: FacilityBillingRateModel[]): boolean {
        let badRange = false;
        let badValue = false;

        rates.forEach((x) => {
            const billRate: FacilityBillingRate = x.facilityBillingRate;
            if (
                this.hasRangeBillingError(billRate.isRangeBilling, billRate.rangeBillingMin, billRate.rangeBillingMax)
            ) {
                badRange = true;
            }

            if (
                this.hasRangeMinMaxError(billRate.rangeBillingMin) ||
                this.hasRangeMinMaxError(billRate.rangeBillingMax)
            ) {
                badValue = true;
            }
        });

        return badRange || badValue;
    }

    /**
     * Verifies that the value is between Min & Max values.
     */
    hasRangeMinMaxError(value: number): boolean {
        if (!value) {
            return false;
        } else {
            const minValid = parseFloat(value.toFixed(2)) >= parseFloat(this.minRangeValue.toFixed(2));
            const maxValid = parseFloat(value.toFixed(2)) <= parseFloat(this.maxRangeValue.toFixed(2));
            return !minValid || !maxValid;
        }
    }

    editProfession(billingRate: FacilityBillingRate): void {
        if (this.canEditProfession) {
            const professionId = billingRate !== null ? billingRate.id : 0;
            const profession = this.createFacilityProfession(billingRate);

            const operation = !professionId || professionId <= 0 ? 'add' : 'update';
            const dialogRef = this.dialog.open(EditProfessionDialogComponent, {
                data: { facilityId: this.facilityId, profession: profession },
                panelClass: [this.panelClass, this.panelClassSm]
            });

            dialogRef
                .afterClosed()
                .pipe(filter((result) => result))
                .subscribe((result) => {
                    if (result) {
                        this.reload$.next(true);
                        const operationResult = operation
                            .replace('add', 'added')
                            .replace('update', 'updated')
                            .replace('delete', 'deleted')
                            .replace('remove', 'removed');

                        this.toasterService.toast(`Profession successfully ${operationResult}.`);
                    }
                });
        }
    }

    removeBillingRate(rateId: number): void {
        const message = 'Are you sure you want to remove selected billing rate? This action cannot be undone.';
        const action = 'YES, REMOVE RATE';
        const title = 'Remove Billing Rate';
        const needCancel = true;
        const cancelText = 'CANCEL';

        this.confirmDialogService
            .show(message, action, title, needCancel, cancelText)
            .pipe(
                filter((result) => result),
                switchMap(() => this.facilityBillingService.removeBillingRate(this.facilityId, rateId))
            )
            .subscribe(
                () => {
                    this.isLoading = false;
                    this.toasterService.toast('Billing rate removed successfully.');
                    this.reload$.next(true);
                },
                () => {
                    this.isLoading = false;
                    this.toasterService.toast('An error occurred while removing billing rate.');
                }
            );
    }

    changeHoursType($event: Event, model: FacilityBillingRateModel): void {
        model.facilityBillingRate.splitedHours = !model.facilityBillingRate.splitedHours;

        if (model.facilityBillingRate.splitedHours) {
            model.facilityBillingRate.shiftDifferentials.forEach((x) => (x.allHours = null));
        } else {
            model.facilityBillingRate.shiftDifferentials.forEach((x) => {
                x.hours8 = null;
                x.hours10 = null;
                x.hours12 = null;
            });
        }

        this.localBillingRates
            .filter((x) => x.id === model.facilityBillingRate.id)
            .forEach((x) => (x.splitedHours = model.facilityBillingRate.splitedHours));
        this.dataEdited = true;
        this.editMode.emit(true);
        $event.preventDefault();
    }

    addShiftDifferential($event: Event, model: FacilityBillingRateModel): void {
        if (!model.facilityBillingRate.shiftDifferentials) {
            model.facilityBillingRate.shiftDifferentials = [];
        }

        const newItem = new FacilityBillingShiftDifferential();
        newItem.facilityBillingRateId = model.facilityBillingRate.id;
        model.facilityBillingRate.shiftDifferentials.push(newItem);
        this.dataEdited = true;
        this.editMode.emit(true);
        $event.preventDefault();
    }

    /**
     * This sets all of the shift type to the entered value
     * @param shift
     * @param value
     */
    setAllShifts(shift: FacilityBillingShiftDifferential, value: boolean): void {
        shift.day = value;
        shift.mid = value;
        shift.night = value;
        shift.weekendDay = value;
        shift.weekendMid = value;
        shift.weekendNight = value;
    }

    /**
     * Creates a "Default" Shift data which is:
     * 1) Shift Type = "All Hours" or Combined
     * 2) All shift type set to true (ie: Day, Evening, Night,...).
     * @param rateModel
     */
    createDefaultShift(billingRateId: number): FacilityBillingShiftDifferential {
        const newItem = new FacilityBillingShiftDifferential();
        newItem.facilityBillingRateId = billingRateId;
        // Default:  check all shift types
        this.setAllShifts(newItem, true);
        return newItem;
    }

    /**
     * This loops thru all Professions and creates a Default Shift Data if there are no existing shift data yet.
     */
    setupDefaultShiftData(): void {
        this.localBillingRates.forEach((rate) => {
            if (!rate.shiftDifferentials) {
                rate.shiftDifferentials = [];
            }

            if (rate.shiftDifferentials.length === 0) {
                const newItem = this.createDefaultShift(rate.id);
                rate.shiftDifferentials.push(newItem);
            }
        });
    }

    deleteWarningDialog() {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                message:
                    'Saving your changes will remove 1 or more existing Billing Rates. Shift visibility, ability to fill the shift, and the hourly rate display may be impacted . Are you sure you want to continue?',
                action: 'YES, SAVE CHANGES',
                title: 'Save Changes',
                needCancel: true,
                needCross: false,
                cancelText: 'CANCEL'
            },
            panelClass: 'nova__dialog'
        });
        return dialogRef.componentInstance.onClose.pipe(map((result) => result));
    }

    saveChanges(rates: FacilityBillingRateModel[]): void {
        if (this.deletedShiftDifferential) {
            this.deleteWarningDialog().subscribe((results) => {
                if (!results) {
                    this.save(rates);
                }
            });
        } else {
            this.save(rates);
        }
    }

    save(rates: FacilityBillingRateModel[]) {
        if (this.hasRangeBillingErrors(rates)) {
            this.toasterService.toast('Range Billing Min must be less than or equal to Range Billing Max.');
            return;
        }

        this.isLoading = true;
        this.facilityBillingService
            .saveBillingRateChanges(
                this.facilityId,
                this.localBillingRates.filter((f) => f.id > 0)
            )
            .pipe(
                untilDestroyed(this),
                catchError(() => {
                    this.isLoading = false;
                    this.toasterService.toast('An error occurred while saving billing rates.');
                    return of({});
                })
            )
            .subscribe(
                () => {
                    this.isLoading = false;
                    this.deletedShiftDifferential = false;
                    this.dataEdited = false;
                    this.editMode.emit(false);
                    this.toasterService.toast('Billing rate saved successfully.');
                    this.reload$.next(true);
                },
                () => {
                    this.isLoading = false;
                    this.toasterService.toast('An error occurred while saving billing rates.');
                }
            );
    }

    removeShiftDifferential(
        model: FacilityBillingRateModel,
        shiftDifferential: FacilityBillingShiftDifferential
    ): void {
        const removedItem = model.facilityBillingRate.shiftDifferentials.indexOf(shiftDifferential);
        model.facilityBillingRate.shiftDifferentials.splice(removedItem, 1);

        this.localBillingRates = this.localBillingRates.filter((x) => x.id !== model.facilityBillingRate.id);
        this.localBillingRates = this.localBillingRates.concat(model.facilityBillingRate);
        this.deletedShiftDifferential = true;
        this.dataEdited = true;
        this.editMode.emit(true);
    }

    getShiftDifferentials(shiftDifferential: FacilityBillingShiftDifferential): any[] {
        const result = [];

        if (shiftDifferential.day) {
            result.push(this.facilityBillingRateType.day.toString());
        }

        if (shiftDifferential.mid) {
            result.push(this.facilityBillingRateType.mid.toString());
        }

        if (shiftDifferential.night) {
            result.push(this.facilityBillingRateType.night.toString());
        }

        if (shiftDifferential.weekendDay) {
            result.push(this.facilityBillingRateType.weekendDay.toString());
        }

        if (shiftDifferential.weekendMid) {
            result.push(this.facilityBillingRateType.weekendMid.toString());
        }

        if (shiftDifferential.weekendNight) {
            result.push(this.facilityBillingRateType.weekendNight.toString());
        }

        return result;
    }

    updateShiftDifferentials(
        $event = null,
        shiftDifferential: FacilityBillingShiftDifferential,
        billingRate: FacilityBillingRate
    ): void {
        if ($event.value) {
            this.setAllShifts(shiftDifferential, false);
            $event.value.forEach((value) => (shiftDifferential[FacilityBillingRateType[value]] = true));

            this.localBillingRates = this.localBillingRates.filter((x) => x.id !== billingRate.id);
            this.localBillingRates = this.localBillingRates.concat(billingRate);
        }
    }

    undo() {
        this.loadRates();
        this.deletedShiftDifferential = false;
        this.dataEdited = false;
        this.editMode.emit(false);
    }

    updateOnBlur(e) {
        this.dataEdited = true;
        this.editMode.emit(true);
    }

    ngOnDestroy(): void {
        this._cleanup$.next(true);
        this._cleanup$.complete();
    }

    private createFacilityProfession(billingRate: FacilityBillingRate): FacilityProfession {
        const professionId = billingRate !== null ? billingRate.id : 0;
        const profession = new FacilityProfession();

        profession.expertises = [];
        profession.id = professionId;
        profession.facilityId = this.facilityId;
        profession.billingRateType =
            billingRate && billingRate.billingRateType !== null
                ? BillingRateType[billingRate.billingRateType.toUpperCase()]
                : BillingRateType.TRAVEL;
        profession.localTraveler = billingRate && billingRate.isLocalTraveler ? billingRate.isLocalTraveler : false;
        profession.rangeBilling = billingRate && billingRate.isRangeBilling ? billingRate.isRangeBilling : false;
        profession.professionId = billingRate && billingRate.professionId ? billingRate.professionId : 0;

        if (billingRate !== null) {
            profession.expertises = billingRate.expertises;
        }
        return profession;
    }
}
